import { useConsent } from '@/hooks/analytics'
import { cn } from '@/lib/utils'

type CookieConsentBannerProps = {
  privacyPolicyHref?: string
  className?: string
}

export const CookieConsentBanner = ({
  privacyPolicyHref = '/privacy',
  className,
}: CookieConsentBannerProps) => {
  const { hasDecided, accept, reject } = useConsent()

  if (hasDecided) return null

  return (
    <div
      role="region"
      aria-live="polite"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-description"
      className={cn(
        'pointer-events-none fixed inset-x-0 bottom-0 z-50 px-4 pb-4 sm:px-6 sm:pb-6',
        className
      )}
    >
      <div className="pointer-events-auto mx-auto flex max-w-3xl flex-col gap-4 rounded-2xl border border-neutral-200 bg-white p-6 shadow-2xl sm:flex-row sm:items-start sm:gap-6">
        <div className="flex-1">
          <h2
            id="cookie-banner-title"
            className="text-base font-semibold text-neutral-900"
          >
            Your privacy matters
          </h2>
          <p
            id="cookie-banner-description"
            className="mt-1 text-sm leading-relaxed text-neutral-600"
          >
            We use cookies and analytics tools to understand how visitors use
            Benepicks so we can improve the product. Strictly necessary cookies
            keep you signed in and are always on. Read our{' '}
            <a
              href={privacyPolicyHref}
              className="font-medium text-orange-primary underline underline-offset-2 hover:text-orange-hover"
            >
              Privacy Policy
            </a>
            .
          </p>
        </div>
        <div
          role="group"
          aria-label="Cookie consent options"
          className="flex flex-shrink-0 flex-col-reverse gap-2 sm:flex-row sm:items-center sm:gap-3 sm:self-end"
        >
          <button
            type="button"
            onClick={reject}
            className="rounded-lg px-4 py-2.5 text-sm font-medium text-neutral-700 hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-2"
          >
            Reject
          </button>
          <button
            type="button"
            onClick={accept}
            className="rounded-lg bg-orange-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-orange-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-2"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  )
}
